import { Fragment, useState } from "react";
import { Container, Form, Row, Col } from "reactstrap";
import { useForm } from "react-hook-form";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { PageHeader } from "../../Components/common/PageHeader";
import { toast } from "react-toastify";
import { CountryForm } from "../../Components/countriesAvailable/CountryForm";
import { CountriesAvailableList } from "../../Components/countriesAvailable/CountriesAvailableList";

const cleanCurrency = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    return Number(
      value
        .replace(/\./g, "")
        .replace(/[^0-9,]/g, "")
        .replace(",", ".")
    );
  }
  return 0;
};

export const CountriesAvailable = () => {
  const { postData, patchData, getData, invalidateQuery } = useDataFetch();

  const [editMode, setEditMode] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      country_id: "",
      name: "",
      currency: "",
      paymentGateways: [],
      pointValue: '',
      sanitaryRegistration: "",   
      personalDataAuthorization: "",
      termsAndConditions: "",
    },
  });

  const { data: countriesData, isLoading } = getData(
    ["countries-available"],
    `/product/countries-available/all`
  );

  const { mutateAsync: createCountry } = postData("/product/countries-available", {}, {
    onSuccess: (res) => {
      if (res.message === "create_country_available_success") {
        toast.success("País creado exitosamente!");
        invalidateQuery(["countries-available"]);
        reset();
      } else {
        toast.error("No se pudo crear el país");
      }
    },
  });

  const { mutateAsync: updateCountry } = patchData(
    `/product/countries-available`,
    {},
    {
      onSuccess: (res) => {
        if (res.message === "update_country_available_success") {
          toast.success("País actualizado exitosamente!");
          invalidateQuery(["countries-available"]);
          cancelEdit();
        } else {
          toast.error("No se pudo actualizar el país");
        }
      },
    }
  );

  const { mutateAsync: toggleStatus } = patchData(
    `/product/countries-available/toggle-status`,
    {},
    {
      onSuccess: () => {
        toast.success("Estado del país actualizado");
        invalidateQuery(["countries-available"]);
      },
    }
  );

  const onSubmit = async (data) => {
    const cleanedPointValue = cleanCurrency(data.pointValue);


    if (!cleanedPointValue || cleanedPointValue <= 0) {
      toast.error("El valor de puntos debe ser mayor a cero.");
      return;
    }

    if (!data.sanitaryRegistration) {
      toast.error("Sanitary Registration PDF is required.");
      return;
    }
    if (!data.personalDataAuthorization) {
      toast.error("Personal Data Authorization PDF is required.");
      return;
    }
    if (!data.termsAndConditions) {
      toast.error("Terms and Conditions PDF is required.");
      return;
    }


    const payload = {
      ...data,
      pointValue: cleanedPointValue,
    };

    try {
      if (editMode && selectedCountry) {
        await updateCountry({
          id: selectedCountry.id,
          ...payload,
        });
      } else {
        await createCountry(payload);
      }
    } catch (error) {
      toast.error("Error al guardar el país");
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await toggleStatus({ id });
    } catch (error) {
      toast.error("Error actualizando estado del país");
    }
  };

  const handleEditCountry = (country) => {
    setEditMode(true);
    setSelectedCountry(country);

    setValue("country_id", country.country_id);
    setValue("name", country.name);
    setValue("currency", country.currency);
    setValue("paymentGateways", country.paymentGateways);
    setValue("pointValue", country.pointValue);
    setValue("sanitaryRegistration", country.sanitaryRegistration || "");
    setValue("personalDataAuthorization", country.personalDataAuthorization || "");
    setValue("termsAndConditions", country.termsAndConditions || "");

  };

  const cancelEdit = () => {
    setEditMode(false);
    setSelectedCountry(null);
    reset();
    setValue("pointValue","");
    setValue("sanitaryRegistration","");
    setValue("personalDataAuthorization","");
    setValue("termsAndConditions","");
  };

  return (
    <Fragment>
      <PageHeader title="Países disponibles" crumbs={[{ label: "Países" }, { label: "Países disponibles" }]} />
      <Container fluid className="credit-card px-0">
        <Form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
          <Row className="g-3">
            <Col xl="4" lg="5" md="12">
              <CountryForm
                control={control}
                errors={errors}
                isSubmitting={isSubmitting}
                watch={watch}
                getValues={getValues}
                setValue={setValue}
                trigger={trigger}
                editMode={editMode}
                cancelEdit={cancelEdit}
              />
            </Col>
            <Col xl="8" lg="7" md="12">
              <CountriesAvailableList
                data={countriesData?.data?.countries ?? []}
                isLoading={isLoading}
                onToggleStatus={handleToggleStatus}
                onEditCountry={handleEditCountry}
              />
            </Col>
          </Row>
        </Form>
      </Container>
    </Fragment>
  );
};
